import { AppStore } from '../state/store';
import { DEFAULT_SUPPORT_PARAMS, SupportParams, suppressRangeForAttach } from '../core/supports/generate';
import { SupportFoot, SupportOverride, mergeFeetMembers } from '../core/supports/types';
import { bindPanelCollapse, panelEnd, panelStart } from './collapsiblePanel';
import { setSidebarTab, sidebarPane } from './sidebarTabs';

type NumParamKey = {
  [K in keyof SupportParams]: SupportParams[K] extends number ? K : never;
}[keyof SupportParams];

type FootSel = { attachS: number; role: 'main' | 'triangle' };

const PRIMARY: { key: NumParamKey; label: string; min: number; max: number; step: number }[] = [
  { key: 'supportCount', label: 'Support count', min: 1, max: 200, step: 1 },
  { key: 'trackSpacing', label: 'Track spacing (m)', min: 2, max: 30, step: 0.5 },
  { key: 'maxSpan', label: 'Max span (m, plan)', min: 4, max: 30, step: 0.5 },
  { key: 'columnRadius', label: 'Column radius (m)', min: 0.05, max: 0.6, step: 0.01 },
  { key: 'braceRadius', label: 'Brace radius (m)', min: 0.03, max: 0.4, step: 0.01 },
  { key: 'braceMinHeight', label: 'Brace min height (m)', min: 0, max: 40, step: 0.5 },
  { key: 'beamHeightGridOffset', label: 'Beam height grid offset (m)', min: 0, max: 20, step: 0.5 },
  { key: 'beamHeightGridStep', label: 'Beam height grid step (m)', min: 1, max: 30, step: 0.5 },
  { key: 'attachStub', label: 'Attach stub (m)', min: 0, max: 10, step: 0.05 },
  { key: 'attachStubRadius', label: 'Stub top radius (m)', min: 0.05, max: 0.8, step: 0.01 },
  { key: 'triangleSpread', label: 'Triangle spread (m)', min: 0, max: 8, step: 0.1 },
  { key: 'triangleMinLeanDeg', label: 'Triangle min lean (°)', min: 0, max: 45, step: 1 },
  { key: 'triangleMinHeight', label: 'Triangle min height (m)', min: 0, max: 40, step: 0.5 },
  { key: 'footerMergeDistance', label: 'Footer merge distance (m)', min: 0, max: 8, step: 0.1 },
];

const SECONDARY: { key: NumParamKey; label: string; min: number; max: number; step: number }[] = [
  { key: 'maxLeanDeg', label: 'Max lean (°)', min: 0, max: 45, step: 1 },
  { key: 'clearance', label: 'Clearance (m)', min: 0.2, max: 3, step: 0.1 },
  { key: 'minTrackHeight', label: 'Min track height (m)', min: 0.2, max: 4, step: 0.1 },
  { key: 'attachDepth', label: 'Attach depth (m)', min: 0.3, max: 2, step: 0.05 },
];

/** Spacing, brace size, and other support-solver knobs — plus pin/suppress/foot overrides. */
export class SupportPanel {
  private el: HTMLElement;
  private debugColors = false;
  private moreOpen = false;
  private onDebugColors: ((on: boolean) => void) | null = null;
  private feet: SupportFoot[] = [];
  private selectedFeet: FootSel[] = [];
  private selectedStubS: number | null = null;
  private onSelectFeet: ((sels: FootSel[]) => void) | null = null;

  constructor(
    private store: AppStore,
    opts?: {
      onDebugColors?: (on: boolean) => void;
      onSelectFeet?: (sels: FootSel[]) => void;
    },
  ) {
    this.el = document.createElement('div');
    sidebarPane('support').appendChild(this.el);
    this.onDebugColors = opts?.onDebugColors ?? null;
    this.onSelectFeet = opts?.onSelectFeet ?? null;
    store.on('doc', () => this.render());
    store.on('supports', () => this.render());
    store.on('selection', () => this.render());
    this.render();
  }

  /** Latest solver feet — used to prefill Override foot XZ. */
  setFeet(feet: SupportFoot[]): void {
    this.feet = feet;
    this.render();
  }

  /** @deprecated Prefer setSelectedFeet — kept for call sites that pass a single pad. */
  setSelectedFoot(sel: FootSel | null): void {
    this.setSelectedFeet(sel ? [sel] : []);
  }

  /** Viewport / panel shared selection of ground pads. */
  setSelectedFeet(sels: FootSel[]): void {
    const next = uniqueFeet(sels);
    if (feetSelEqual(this.selectedFeet, next)) return;
    this.selectedFeet = next;
    if (next.length > 0) this.selectedStubS = null;
    this.render();
    if (next.length > 0) setSidebarTab('support', { expand: true });
  }

  /** Viewport selection of an attach stub (Delete → suppress). */
  setSelectedStub(attachS: number | null): void {
    if (this.selectedStubS === attachS) return;
    if (
      this.selectedStubS != null &&
      attachS != null &&
      Math.abs(this.selectedStubS - attachS) < 1e-4
    ) {
      return;
    }
    this.selectedStubS = attachS;
    if (attachS != null) this.selectedFeet = [];
    this.render();
    if (attachS != null) setSidebarTab('support', { expand: true });
  }

  private render(): void {
    const { supports, supportsEnabled, supportOverrides } = this.store.doc;
    const selectedS = this.selectedNodeS();
    const footInfo = this.footEditorState();
    const canMerge = this.selectedFeet.length >= 2;

    this.el.innerHTML = `
      ${panelStart('supports', 'Supports')}
        <div class="row"><label><input type="checkbox" data-action="enabled" ${supportsEnabled ? 'checked' : ''}> Enabled</label></div>
        <div class="row"><label><input type="checkbox" data-action="debug" ${this.debugColors ? 'checked' : ''}> Debug colours</label></div>
        ${
          canMerge
            ? `<div class="row"><button type="button" data-action="merge-feet">Merge selected feet</button>
                <span class="hint">${this.selectedFeet.map(footLabel).join(' + ')}</span></div>`
            : '<p class="hint">Click a ground pad to move its base. Click a stub, then Delete to hide that support. Ctrl+click extra pads to merge.</p>'
        }
        <div class="row"><label><input type="checkbox" data-action="braceMainOnly" ${supports.braceMainOnly ? 'checked' : ''}> Side beams on main only <span class="hint">(same-piece triangle still laced)</span></label></div>
        ${PRIMARY.map((p) => slider(p.key, p.label, supports[p.key], p.min, p.max, p.step)).join('')}
        <div class="row"><label><input type="checkbox" data-action="allowTriangleMerge" ${supports.allowTriangleMerge ? 'checked' : ''}> Allow triangle merge <span class="hint">(main ↔ triangle footers)</span></label></div>
        <details class="support-more"${this.moreOpen ? ' open' : ''}><summary>More</summary>
          ${SECONDARY.map((p) => slider(p.key, p.label, supports[p.key], p.min, p.max, p.step)).join('')}
        </details>
        <div class="row">
          <button type="button" data-action="reset">Reset defaults</button>
        </div>
        <h4>Overrides</h4>
        ${
          selectedS == null
            ? '<p class="hint">Select a node to pin or suppress.</p>'
            : `<div class="row">
                <button type="button" data-action="force">Force at node (s=${selectedS.toFixed(1)})</button>
                <button type="button" data-action="suppress">Suppress near node</button>
              </div>`
        }
        ${
          this.selectedStubS != null
            ? `<p class="hint">Stub selected @ s=${this.selectedStubS.toFixed(1)} m — press Delete to suppress.</p>`
            : ''
        }
        ${footOverrideBlock(footInfo)}
        ${overrideList(supportOverrides, this.selectedFeet, this.selectedStubS)}
        ${
          supportOverrides.length > 0
            ? '<div class="row"><button type="button" data-action="clear-overrides">Clear all</button></div>'
            : ''
        }
      ${panelEnd()}`;

    bindPanelCollapse(this.el);

    this.el.querySelectorAll<HTMLInputElement>('input[type="range"]').forEach((input) => {
      const valEl = input.parentElement!.querySelector('.val')!;
      input.addEventListener('input', () => {
        valEl.textContent = fmt(parseFloat(input.value));
      });
      input.addEventListener('change', () => {
        const key = input.dataset.key as NumParamKey;
        let value = parseFloat(input.value);
        if (key === 'supportCount') value = Math.max(1, Math.round(value));
        this.store.commit((d) => {
          d.supports[key] = value;
        });
        this.store.emit('supports');
      });
    });

    this.el.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((input) => {
      input.addEventListener('change', () => {
        const action = input.dataset.action;
        if (action === 'enabled') {
          this.store.commit((d) => {
            d.supportsEnabled = input.checked;
          });
          this.store.emit('supports');
        } else if (action === 'braceMainOnly') {
          this.store.commit((d) => {
            d.supports.braceMainOnly = input.checked;
          });
          this.store.emit('supports');
        } else if (action === 'allowTriangleMerge') {
          this.store.commit((d) => {
            d.supports.allowTriangleMerge = input.checked;
          });
          this.store.emit('supports');
        } else if (action === 'debug') {
          this.debugColors = input.checked;
          this.onDebugColors?.(this.debugColors);
        } else if (action === 'overrideFoot') {
          this.toggleFootOverride(input.checked);
        }
      });
    });

    this.el.querySelectorAll<HTMLButtonElement>('button[data-action]').forEach((btn) => {
      btn.addEventListener('click', () => this.onAction(btn.dataset.action!));
    });

    this.el.querySelectorAll<HTMLLIElement>('li[data-select-foot]').forEach((li) => {
      li.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('button[data-remove]')) return;
        const s = parseFloat(li.dataset.footS!);
        const role = (li.dataset.footRole === 'triangle' ? 'triangle' : 'main') as
          | 'main'
          | 'triangle';
        if (!isFinite(s)) return;
        this.selectFeetFromList([{ attachS: s, role }]);
      });
    });

    this.el.querySelectorAll<HTMLLIElement>('li[data-select-merge]').forEach((li) => {
      li.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('button[data-remove]')) return;
        const sList = (li.dataset.mergeS ?? '')
          .split(',')
          .map((v) => parseFloat(v))
          .filter((v) => isFinite(v));
        const roles = (li.dataset.mergeRoles ?? '').split(',');
        if (sList.length < 2) return;
        this.selectFeetFromList(
          sList.map((attachS, i) => ({
            attachS,
            role: (roles[i] === 'triangle' ? 'triangle' : 'main') as 'main' | 'triangle',
          })),
        );
      });
    });

    this.el.querySelectorAll<HTMLButtonElement>('button[data-remove]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.remove!, 10);
        this.store.commit((d) => {
          d.supportOverrides.splice(idx, 1);
        });
        this.store.emit('supports');
      });
    });

    this.el.querySelectorAll<HTMLInputElement>('input[data-foot-axis]').forEach((input) => {
      input.addEventListener('change', () => {
        const axis = input.dataset.footAxis as 'x' | 'z';
        const value = parseFloat(input.value);
        if (!isFinite(value) || !footInfo) return;
        this.upsertForceFoot(footInfo.attachS, footInfo.role, {
          x: axis === 'x' ? value : footInfo.x,
          z: axis === 'z' ? value : footInfo.z,
        });
      });
    });

    const more = this.el.querySelector<HTMLDetailsElement>('details.support-more');
    more?.addEventListener('toggle', () => {
      this.moreOpen = more.open;
    });
  }

  private footEditorState(): {
    attachS: number;
    role: 'main' | 'triangle';
    x: number;
    z: number;
    forced: boolean;
  } | null {
    if (this.selectedFeet.length === 1) {
      const sel = this.selectedFeet[0];
      const ov = findForceFoot(this.store.doc.supportOverrides, sel.attachS, sel.role);
      const foot =
        this.feet.find(
          (f) => f.role === sel.role && Math.abs(f.attachS - sel.attachS) < 1e-4,
        ) ?? null;
      const x = ov?.x ?? foot?.pos.x;
      const z = ov?.z ?? foot?.pos.z;
      if (x == null || z == null) return null;
      return {
        attachS: sel.attachS,
        role: sel.role,
        x,
        z,
        forced: !!ov,
      };
    }

    if (this.selectedFeet.length > 1) return null;

    const s = this.selectedNodeS();
    if (s == null) return null;
    const foot = nearestFoot(this.feet, s, 'main');
    if (!foot) return null;
    const ov = findForceFoot(this.store.doc.supportOverrides, foot.attachS, foot.role);
    return {
      attachS: foot.attachS,
      role: foot.role,
      x: ov?.x ?? foot.pos.x,
      z: ov?.z ?? foot.pos.z,
      forced: !!ov,
    };
  }

  /** Pick override(s) from the list → highlight pad(s) in the viewport. */
  private selectFeetFromList(sels: FootSel[]): void {
    this.selectedStubS = null;
    this.selectedFeet = uniqueFeet(sels);
    this.onSelectFeet?.(this.selectedFeet);
    this.render();
  }

  private toggleFootOverride(on: boolean): void {
    const info = this.footEditorState();
    if (!info) return;
    if (on) {
      this.upsertForceFoot(info.attachS, info.role, { x: info.x, z: info.z });
      this.selectedFeet = [{ attachS: info.attachS, role: info.role }];
      this.onSelectFeet?.(this.selectedFeet);
    } else {
      this.store.commit((d) => {
        d.supportOverrides = d.supportOverrides.filter((o) => {
          if (o.kind !== 'forceFoot') return true;
          if (Math.abs(o.s - info.attachS) > 1e-4) return true;
          return (o.role ?? 'main') !== info.role;
        });
      });
      this.store.emit('supports');
    }
  }

  private upsertForceFoot(
    attachS: number,
    role: 'main' | 'triangle',
    xz: { x: number; z: number },
  ): void {
    this.store.commit((d) => {
      const idx = d.supportOverrides.findIndex(
        (o) =>
          o.kind === 'forceFoot' &&
          Math.abs(o.s - attachS) < 1e-4 &&
          (o.role ?? 'main') === role,
      );
      const next: SupportOverride = { kind: 'forceFoot', s: attachS, x: xz.x, z: xz.z, role };
      if (idx >= 0) d.supportOverrides[idx] = next;
      else d.supportOverrides.push(next);
    });
    this.store.emit('supports');
  }

  private mergeSelectedFeet(): void {
    if (this.selectedFeet.length < 2) return;
    const [a, b, ...rest] = this.selectedFeet;
    this.store.commit((d) => {
      const exists = d.supportOverrides.some(
        (o) => o.kind === 'mergeFeet' && mergeFeetMatch(o, this.selectedFeet),
      );
      if (exists) return;
      d.supportOverrides.push({
        kind: 'mergeFeet',
        sA: a.attachS,
        roleA: a.role,
        sB: b.attachS,
        roleB: b.role,
        ...(rest.length > 0
          ? { extra: rest.map((f) => ({ s: f.attachS, role: f.role })) }
          : {}),
      });
    });
    this.store.emit('supports');
  }

  private onAction(action: string): void {
    if (action === 'reset') {
      this.store.commit((d) => {
        d.supports = { ...DEFAULT_SUPPORT_PARAMS };
      });
      this.store.emit('supports');
      return;
    }
    if (action === 'force') {
      const s = this.selectedNodeS();
      if (s == null) return;
      this.store.commit((d) => {
        d.supportOverrides.push({ kind: 'force', s });
      });
      this.store.emit('supports');
      return;
    }
    if (action === 'suppress') {
      const s = this.selectedNodeS();
      if (s == null) return;
      const range = suppressRangeForAttach(
        s,
        this.store.runtime.totalLength,
        this.store.doc.supports.supportCount,
      );
      this.store.commit((d) => {
        d.supportOverrides.push({
          kind: 'suppress',
          from: range.from,
          to: range.to,
        });
      });
      this.store.emit('supports');
      return;
    }
    if (action === 'merge-feet') {
      this.mergeSelectedFeet();
      return;
    }
    if (action === 'clear-overrides') {
      this.store.commit((d) => {
        d.supportOverrides = [];
      });
      this.store.emit('supports');
    }
  }

  private selectedNodeS(): number | null {
    if (this.store.selection.size !== 1) return null;
    const id = [...this.store.selection][0];
    const idx = this.store.doc.track.nodes.findIndex((n) => n.id === id);
    if (idx < 0) return null;
    const cl = this.store.runtime.centerline;
    if (cl.totalLength < 1e-6) return null;
    return cl.spanStartS(idx);
  }
}

function footOverrideBlock(
  info: { attachS: number; role: 'main' | 'triangle'; x: number; z: number; forced: boolean } | null,
): string {
  if (!info) return '';
  const roleLabel = info.role === 'triangle' ? 'triangle' : 'main';
  return `<div class="row override-row">
    <label class="override-toggle"><input type="checkbox" data-action="overrideFoot"${
      info.forced ? ' checked' : ''
    }> Override foot XZ <span class="hint">(${roleLabel} @ s=${info.attachS.toFixed(1)})</span></label>
    <div class="override-controls">
      <label>X</label>
      <input type="number" step="0.5" data-foot-axis="x" value="${info.x.toFixed(2)}"${
        info.forced ? '' : ' disabled'
      }>
      <label>Z</label>
      <input type="number" step="0.5" data-foot-axis="z" value="${info.z.toFixed(2)}"${
        info.forced ? '' : ' disabled'
      }>
    </div>
  </div>
  <p class="hint">Drag a ground pad in the viewport to force-move its base point.</p>`;
}

function slider(key: string, label: string, value: number, min: number, max: number, step: number): string {
  return `<div class="row"><label>${label}</label>
    <input type="range" data-key="${key}" min="${min}" max="${max}" step="${step}" value="${value}">
    <span class="val">${fmt(value)}</span></div>`;
}

function overrideList(
  overrides: SupportOverride[],
  selectedFeet: FootSel[],
  selectedStubS: number | null,
): string {
  if (overrides.length === 0) return '<p class="hint">No overrides.</p>';
  return `<ul class="override-list">${overrides
    .map((o, i) => {
      let label: string;
      let selectAttrs = '';
      let selectedClass = '';
      switch (o.kind) {
        case 'force':
          label = `Force @ s=${o.s.toFixed(1)} m`;
          break;
        case 'suppress': {
          label = `Suppress ${o.from.toFixed(1)}–${o.to.toFixed(1)} m`;
          if (
            selectedStubS != null &&
            selectedStubS >= Math.min(o.from, o.to) - 1e-4 &&
            selectedStubS <= Math.max(o.from, o.to) + 1e-4
          ) {
            selectedClass = ' selected';
          }
          break;
        }
        case 'forceFoot': {
          const role = o.role ?? 'main';
          label = `Foot ${role} @ s=${o.s.toFixed(1)} → (${o.x.toFixed(1)}, ${o.z.toFixed(1)})`;
          selectAttrs = ` data-select-foot data-foot-s="${o.s}" data-foot-role="${role}" title="Select foot in viewport"`;
          if (
            selectedFeet.length === 1 &&
            selectedFeet[0].role === role &&
            Math.abs(selectedFeet[0].attachS - o.s) < 1e-4
          ) {
            selectedClass = ' selected';
          }
          break;
        }
        case 'mergeFeet': {
          const members = mergeFeetMembers(o);
          label = `Merge ${members.map((m) => `${m.role}@${m.s.toFixed(1)}`).join(' + ')}`;
          selectAttrs = ` data-select-merge data-merge-s="${members.map((m) => m.s).join(',')}" data-merge-roles="${members.map((m) => m.role).join(',')}" title="Select merged feet in viewport"`;
          if (mergeFeetMatch(o, selectedFeet)) {
            selectedClass = ' selected';
          }
          break;
        }
        default: {
          const _exhaustive: never = o;
          label = String(_exhaustive);
        }
      }
      return `<li class="override-item${selectedClass}"${selectAttrs}><span>${label}</span> <button type="button" data-remove="${i}">×</button></li>`;
    })
    .join('')}</ul>`;
}

function findForceFoot(
  overrides: SupportOverride[],
  s: number,
  role: 'main' | 'triangle',
): Extract<SupportOverride, { kind: 'forceFoot' }> | null {
  for (const o of overrides) {
    if (o.kind !== 'forceFoot') continue;
    if (Math.abs(o.s - s) > 1e-4) continue;
    if ((o.role ?? 'main') !== role) continue;
    return o;
  }
  return null;
}

function mergeFeetMatch(
  o: Extract<SupportOverride, { kind: 'mergeFeet' }>,
  sels: FootSel[],
): boolean {
  const members = mergeFeetMembers(o);
  if (members.length !== sels.length) return false;
  return members.every((m) =>
    sels.some((s) => s.role === m.role && Math.abs(s.attachS - m.s) < 1e-4),
  );
}

function uniqueFeet(sels: FootSel[]): FootSel[] {
  const next: FootSel[] = [];
  for (const sel of sels) {
    if (next.some((s) => s.role === sel.role && Math.abs(s.attachS - sel.attachS) < 1e-4)) {
      continue;
    }
    next.push(sel);
  }
  return next;
}

function nearestFoot(
  feet: SupportFoot[],
  s: number,
  role: 'main' | 'triangle',
): SupportFoot | null {
  let best: SupportFoot | null = null;
  let bestD = Infinity;
  for (const f of feet) {
    if (f.role !== role) continue;
    const d = Math.abs(f.attachS - s);
    if (d < bestD) {
      bestD = d;
      best = f;
    }
  }
  return best && bestD < 8 ? best : null;
}

function feetSelEqual(a: FootSel[], b: FootSel[]): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (s, i) => s.role === b[i].role && Math.abs(s.attachS - b[i].attachS) < 1e-4,
  );
}

function footLabel(sel: FootSel): string {
  return `${sel.role}@${sel.attachS.toFixed(1)}`;
}

function fmt(v: number): string {
  return v >= 10 ? v.toFixed(0) : v >= 1 ? v.toFixed(1) : v.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}
